/**
 * Date helpers for the split Start Date / End Date promo fields (HTT-9780).
 */

/**
 * Earliest start date among stay ranges that are still editable — skips locked
 * (elapsed) ranges and rows without a start date. Drives the stay-before-booking
 * check and blackout ordering (EO-1492). Returns null when none qualify.
 *
 * @param {Array<{dates: Date[]|null, isLocked?: boolean}>} ranges
 * @returns {Date|null}
 */
export const getEarliestStayStart = (ranges) =>
  (ranges || []).reduce((earliest, range) => {
    const start = range?.dates?.[0];
    if (!start || range.isLocked) return earliest;
    return !earliest || start < earliest ? start : earliest;
  }, null);

/** Normalizes a date to local midnight, in milliseconds. */
export const startOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

/**
 * A promo that has already started can still be extended, but its start date is
 * settled. Today counts as not past, so a promo starting today stays editable.
 */
export const isPastDate = (value) =>
  !!value && startOfDay(value) < startOfDay(new Date());

/**
 * Re-anchors a date parsed from a "YYYY-MM-DD" string to LOCAL midnight.
 *
 * ⚠ `new Date("2026-08-01")` is parsed as UTC midnight, not local. West of UTC that lands on the
 * previous local day, so a picker fed the raw value shows — and echoes back — a date one day early.
 * East of UTC it lands mid-morning, which is the right day but not midnight, so day comparisons that
 * expect a clean boundary go wrong instead.
 *
 * Adding the offset moves it to local midnight, which is what every picker and `startOfDay` compare
 * against.
 *
 * @param {Date|null} date - a Date parsed from a date-only string
 * @returns {Date|null} the same calendar day at local midnight
 */
export const adjustDateForTimezone = (date) => {
  if (!date) return null;
  const adjustedDate = new Date(date.getTime());
  adjustedDate.setMinutes(
    adjustedDate.getMinutes() + adjustedDate.getTimezoneOffset()
  );
  return adjustedDate;
};
